import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Dropdown, Icon, Input, ModalWindow, Select, Table, useLocalizeMessage, } from 'libs.nucleus';
const validateLimit = (input) => {
    return input >= 1 && input <= 100;
};
const directionOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
];
const operatorOptions = [
    { label: '=', value: 'equals' },
    { label: '≥', value: 'gte' },
    { label: '>', value: 'gt' },
    { label: '≤', value: 'lte' },
    { label: '<', value: 'lt' },
    { label: '≠', value: 'notEquals' },
    { label: 'contains', value: 'contains' },
    { label: 'not contains', value: 'notContains' },
];
const AliasInput = ({ fieldId, initialAlias, onAliasChange }) => {
    const [localValue, setLocalValue] = useState(initialAlias);
    // Update local value when initial value changes (like on reset)
    useEffect(() => {
        setLocalValue(initialAlias);
    }, [initialAlias]);
    const handleBlur = () => {
        onAliasChange(fieldId, localValue);
    };
    return (_jsx(Input, { id: `alias-${fieldId}`, value: localValue, onChange: (e) => setLocalValue(e.target.value), onBlur: handleBlur, width: 'full' }));
};
// Custom hook to create table columns outside component scope
const useRenameColumns = (translate, selectedDataFields, aliases, positions, handleAliasChange, handleMoveField) => {
    return useMemo(() => [
        {
            header: translate('Original value'),
            accessorKey: 'shortTitle',
            size: 300,
        },
        {
            header: translate('Current value'),
            cell: ({ row }) => {
                const currentAlias = aliases[row.original.id] || row.original.shortTitle;
                return _jsx(AliasInput, { fieldId: row.original.id, initialAlias: currentAlias, onAliasChange: handleAliasChange });
            },
            size: 300,
        },
        {
            header: '',
            id: 'options',
            cell: ({ row }) => {
                const currentPosition = positions[row.original.id] || 1;
                const maxPosition = selectedDataFields?.length || 1;
                const dropdownOptions = [
                    {
                        id: 'move-up',
                        content: translate('Move up'),
                        onClick: () => handleMoveField(row.original.id, 'up'),
                        disabled: currentPosition === 1,
                    },
                    {
                        id: 'move-down',
                        content: translate('Move down'),
                        onClick: () => handleMoveField(row.original.id, 'down'),
                        disabled: currentPosition === maxPosition,
                    },
                ];
                return (_jsx("section", { className: 'flex flex-col items-end', children: _jsx(Dropdown, { position: 'bottom-end', items: dropdownOptions, variant: 'kebab' }) }));
            },
        },
    ], 
    // Only depend on stable values, not aliases
    [translate, selectedDataFields, positions, handleAliasChange, handleMoveField]);
};
export const DataBuilderSettingsModal = ({ isOpen, onClose, onApply, selectedDataFields, currentOrderSettings, currentOutputArrangement, currentLimit, currentFilterSettings, selectedDataView, dataTransferViews, getDimensionValues, }) => {
    const translate = useLocalizeMessage();
    // Split state into separate pieces - aliases and positions separated for better performance
    const [limit, setLimit] = useState(currentLimit || 100);
    const [orderByFields, setOrderByFields] = useState([
        {
            orderBy: '',
            direction: 'asc',
        },
    ]);
    const [aliases, setAliases] = useState({});
    const [positions, setPositions] = useState({});
    const [filters, setFilters] = useState([]);
    const [isFieldValid, setIsFieldValid] = useState({
        limit: true,
    });
    const allOrderByOptions = useMemo(() => {
        return selectedDataFields?.map((field) => ({ label: field.shortTitle, value: field.id })) || [];
    }, [selectedDataFields]);
    // Initialize aliases and positions from current settings when modal opens
    useEffect(() => {
        if (selectedDataFields && selectedDataFields.length > 0) {
            const updatedAliases = {};
            const updatedPositions = {};
            selectedDataFields.forEach((field, index) => {
                // Use existing arrangement if available, otherwise use default values
                if (currentOutputArrangement && currentOutputArrangement[field.id]) {
                    updatedAliases[field.id] = currentOutputArrangement[field.id].alias;
                    updatedPositions[field.id] = currentOutputArrangement[field.id].position;
                }
                else {
                    // For new fields, use original name and next available position
                    updatedAliases[field.id] = field.shortTitle;
                    const maxPosition = currentOutputArrangement
                        ? Math.max(0, ...Object.values(currentOutputArrangement).map((item) => item.position))
                        : 0;
                    updatedPositions[field.id] = maxPosition + index + 1;
                }
            });
            setAliases(updatedAliases);
            setPositions(updatedPositions);
        }
    }, [selectedDataFields, currentOutputArrangement]);
    // Initialize limit from current settings
    useEffect(() => {
        if (currentLimit !== undefined) {
            setLimit(currentLimit);
        }
    }, [currentLimit]);
    // Initialize order by fields from current settings
    useEffect(() => {
        if (currentOrderSettings && Object.keys(currentOrderSettings).length > 0 && allOrderByOptions.length > 0) {
            const orderByFromSettings = Object.entries(currentOrderSettings).map(([fieldId, direction]) => ({
                orderBy: fieldId,
                direction: direction,
            }));
            // Ensure we have at least one order by field
            if (orderByFromSettings.length === 0 && allOrderByOptions.length > 0) {
                orderByFromSettings.push({
                    orderBy: allOrderByOptions[0].value,
                    direction: 'asc',
                });
            }
            setOrderByFields(orderByFromSettings);
        }
        else if (allOrderByOptions.length > 0 && orderByFields[0].orderBy === '') {
            // Fallback to default initialization
            setOrderByFields([
                {
                    orderBy: allOrderByOptions[0].value,
                    direction: 'asc',
                },
            ]);
        }
    }, [currentOrderSettings, allOrderByOptions]);
    // Initialize filters from current settings
    useEffect(() => {
        if (currentFilterSettings && currentFilterSettings.length > 0) {
            setFilters(currentFilterSettings);
        }
        else {
            setFilters([]);
        }
    }, [currentFilterSettings]);
    const getAvailableOptionsForNewSelection = (currentIndex) => {
        const selectedOrderByValues = orderByFields
            .map((field, index) => (index !== currentIndex ? field.orderBy : null))
            .filter(Boolean);
        return allOrderByOptions.filter((option) => !selectedOrderByValues.includes(option.value));
    };
    const isFormValid = Object.values(isFieldValid).every((isValid) => isValid) && orderByFields.every((field) => field.orderBy !== '');
    const fieldValidatorFns = {
        limit: validateLimit,
    };
    const handleLimitChange = (value) => {
        setLimit(value);
        setIsFieldValid({ ...isFieldValid, limit: fieldValidatorFns.limit?.(value) ?? true });
    };
    const handleOrderByFieldChange = (index, field, value) => {
        const updatedOrderByFields = [...orderByFields];
        updatedOrderByFields[index] = { ...updatedOrderByFields[index], [field]: value };
        setOrderByFields(updatedOrderByFields);
    };
    const handleAddOrderBy = () => {
        const availableOptions = getAvailableOptionsForNewSelection(-1);
        if (availableOptions.length > 0 && orderByFields.length < allOrderByOptions.length) {
            const newOrderBy = availableOptions[0]?.value || '';
            setOrderByFields([
                ...orderByFields,
                {
                    orderBy: newOrderBy,
                    direction: 'asc',
                },
            ]);
        }
    };
    const handleDeleteOrderBy = (index) => {
        if (index > 0) {
            // Can't delete the first row
            const updatedOrderByFields = orderByFields.filter((_, i) => i !== index);
            setOrderByFields(updatedOrderByFields);
        }
    };
    const handleFilterChange = (index, field, value) => {
        const updatedFilters = [...filters];
        updatedFilters[index] = { ...updatedFilters[index], [field]: value };
        setFilters(updatedFilters);
    };
    const handleAddFilter = () => {
        setFilters([
            ...filters,
            {
                field: '',
                operator: 'equals',
                value: [],
            },
        ]);
    };
    const handleRemoveFilter = (index) => {
        const updatedFilters = filters.filter((_, i) => i !== index);
        setFilters(updatedFilters);
    };
    const handleReloadFilterValues = async (filterIndex) => {
        const filter = filters[filterIndex];
        if (!filter.field) {
            return;
        }
        try {
            // Clear loading state for this field
            setIsLoadingDimensionValues((prev) => ({ ...prev, [filter.field]: true }));
            // Fetch fresh values
            const freshOptions = await getFilterValueOptions(filter.field);
            // Preserve currently selected values that might not be in the fresh response
            const currentSelectedValues = filter.value || [];
            const preservedOptions = [...freshOptions];
            // Add any selected values that aren't in the fresh response
            currentSelectedValues.forEach((selectedValue) => {
                if (!freshOptions.some((option) => option.value === selectedValue)) {
                    preservedOptions.push({ label: selectedValue, value: selectedValue });
                }
            });
            // Update the dimension value options
            setDimensionValueOptions((prev) => ({
                ...prev,
                [filter.field]: preservedOptions,
            }));
        }
        catch (error) {
            console.error(`Error reloading values for field ${filter.field}:`, error);
        }
        finally {
            setIsLoadingDimensionValues((prev) => ({ ...prev, [filter.field]: false }));
        }
    };
    const filterFieldOptions = useMemo(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        const selectedView = viewsArray.find((view) => view.name === selectedDataView.value);
        if (!selectedView) {
            return [];
        }
        return selectedView.dimensions
            .filter((dimension) => dimension.type === 'string')
            .map((dimension) => ({
            label: dimension.shortTitle || dimension.title,
            value: dimension.name,
        }));
    }, [dataTransferViews, selectedDataView]);
    const getFilterValueOptions = async (field) => {
        if (dimensionValueOptions[field]) {
            return dimensionValueOptions[field];
        }
        if (isLoadingDimensionValues[field]) {
            return [];
        }
        setIsLoadingDimensionValues((prev) => ({ ...prev, [field]: true }));
        try {
            const values = await getDimensionValues(field);
            if (values.length === 0) {
                setDimensionValueOptions((prev) => ({ ...prev, [field]: [] }));
                return [];
            }
            const options = values.map((value) => ({ label: value, value }));
            setDimensionValueOptions((prev) => ({ ...prev, [field]: options }));
            return options;
        }
        catch (error) {
            console.error(`Error fetching values for field ${field}:`, error);
            setDimensionValueOptions((prev) => ({ ...prev, [field]: [] }));
            return [];
        }
        finally {
            setIsLoadingDimensionValues((prev) => ({ ...prev, [field]: false }));
        }
    };
    // Add new filter state
    const [dimensionValueOptions, setDimensionValueOptions] = useState({});
    const [isLoadingDimensionValues, setIsLoadingDimensionValues] = useState({});
    // Make handleAliasChange stable with useCallback to prevent unnecessary re-renders
    const handleAliasChange = useCallback((fieldId, newAlias) => {
        setAliases((prevAliases) => ({
            ...prevAliases,
            [fieldId]: newAlias,
        }));
    }, []);
    // Make handleMoveField stable with useCallback
    const handleMoveField = useCallback((fieldId, direction) => {
        setPositions((currentPositions) => {
            const newPositions = { ...currentPositions };
            // Safety check: ensure the field exists in positions
            if (!newPositions[fieldId]) {
                return currentPositions;
            }
            const currentPosition = newPositions[fieldId];
            // Find the field to swap with
            const targetPosition = direction === 'up' ? currentPosition - 1 : currentPosition + 1;
            const targetFieldId = Object.keys(newPositions).find((id) => newPositions[id] === targetPosition);
            if (targetFieldId && newPositions[targetFieldId]) {
                // Swap positions
                newPositions[fieldId] = targetPosition;
                newPositions[targetFieldId] = currentPosition;
            }
            return newPositions;
        });
    }, []);
    const handleResetRenaming = () => {
        const resetAliases = {};
        // Reset all aliases to original values
        selectedDataFields?.forEach((field) => {
            resetAliases[field.id] = field.shortTitle;
        });
        setAliases(resetAliases);
    };
    // Get sorted data for the table based on position
    const sortedFieldsForTable = useMemo(() => {
        if (!selectedDataFields || Object.keys(positions).length === 0) {
            return selectedDataFields || [];
        }
        return [...selectedDataFields].sort((a, b) => {
            const positionA = positions[a.id] || 0;
            const positionB = positions[b.id] || 0;
            return positionA - positionB;
        });
    }, [selectedDataFields, positions]);
    // Use the custom hook for columns
    const renameColumns = useRenameColumns(translate, selectedDataFields, aliases, positions, handleAliasChange, handleMoveField);
    const handleClose = () => {
        onClose();
    };
    const handleApply = () => {
        // Prepare order object from orderByFields
        const order = {};
        orderByFields.forEach((field) => {
            if (field.orderBy) {
                order[field.orderBy] = field.direction;
            }
        });
        // Reconstruct outputArrangement from separate aliases and positions
        const outputArrangement = {};
        selectedDataFields?.forEach((field) => {
            outputArrangement[field.id] = {
                alias: aliases[field.id] || field.shortTitle,
                position: positions[field.id] || 1,
            };
        });
        // Filter out empty filters
        const validFilters = filters.filter((filter) => filter.field && filter.operator && filter.value.length > 0);
        // Call the parent callback with the settings
        onApply({
            limit,
            order,
            outputArrangement,
            filters: validFilters,
        });
    };
    const footerPrimaryActionButton = {
        label: translate('Apply'),
        onClick: handleApply,
        disabled: !isFormValid,
    };
    const footerSecondaryActionButtons = [{ label: translate('Cancel'), onClick: handleClose }];
    return (_jsxs(ModalWindow, { title: translate('Settings'), isOpen: isOpen, closeWindow: handleClose, width: 'full', footerPrimaryActionButton: footerPrimaryActionButton, footerSecondaryActionButtons: footerSecondaryActionButtons, children: [_jsxs("section", { className: 'flex flex-col gap-4 w-full md:w-[50%]', children: [_jsxs("div", { className: 'flex gap-4 items-end', children: [_jsx("div", { className: 'flex-1', children: _jsx(Input, { id: 'limit', label: translate('Preview limit (max 100)'), description: translate('The limit on data rows is only for the preview, which can be 100 rows or lower.'), value: limit, onChange: (e) => handleLimitChange(e.target.value), hasError: !isFieldValid.limit, errorMessage: translate('Invalid limit.'), width: 'full', type: 'number', min: 1, max: 100 }) }), _jsx("div", { className: 'flex items-center justify-center pb-2 w-8' })] }), _jsxs("div", { className: 'flex flex-col gap-4', children: [orderByFields.map((orderByField, index) => {
                                // For display, always include current value + available options
                                const currentOption = allOrderByOptions.find((option) => option.value === orderByField.orderBy);
                                const availableForSelection = getAvailableOptionsForNewSelection(index);
                                // Include current option + available options (removing duplicates)
                                const selectOptions = currentOption && !availableForSelection.find((opt) => opt.value === currentOption.value)
                                    ? [currentOption, ...availableForSelection]
                                    : availableForSelection.length > 0
                                        ? availableForSelection
                                        : allOrderByOptions;
                                // Ensure we always have a valid current option
                                const validCurrentOption = currentOption || selectOptions[0] || { label: '', value: '' };
                                const currentDirection = directionOptions.find((option) => option.value === orderByField.direction) || directionOptions[0];
                                return (_jsxs("div", { className: 'flex gap-4 items-end', children: [_jsxs("div", { className: 'grid grid-cols-2 gap-4 flex-1', children: [_jsx(Select, { label: translate('Order by'), placeholder: '', options: selectOptions, value: validCurrentOption, onChange: (option) => handleOrderByFieldChange(index, 'orderBy', option.value), width: 'full' }), _jsx(Select, { label: translate('Direction'), placeholder: '', options: directionOptions, value: currentDirection, onChange: (option) => handleOrderByFieldChange(index, 'direction', option.value), width: 'full' })] }), index > 0 ? (_jsx("div", { className: 'flex items-center justify-center cursor-pointer hover:opacity-70 pb-2 w-8', onClick: () => handleDeleteOrderBy(index), children: _jsx(Icon, { name: 'delete', size: 'sm', color: 'red' }) })) : (_jsx("div", { className: 'flex items-center justify-center pb-2 w-8' }))] }, orderByField.orderBy));
                            }), orderByFields.length < allOrderByOptions.length && getAvailableOptionsForNewSelection(-1).length > 0 && (_jsxs("div", { className: 'flex items-center gap-2 text-primary text-sm cursor-pointer', onClick: handleAddOrderBy, children: [_jsx(Icon, { name: 'add', size: 'sm', color: 'primary' }), _jsx("span", { children: translate('Add order by') })] }))] }), _jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx("div", { className: 'text-lg font-semibold', children: translate('Filters') }), filters.map((filter) => {
                                const currentField = filterFieldOptions.find((option) => option.value === filter.field);
                                const currentOperator = operatorOptions.find((option) => option.value === filter.operator);
                                const loadedValueOptions = dimensionValueOptions[filter.field] || [];
                                const isLoading = isLoadingDimensionValues[filter.field] || false;
                                // Create options that include both loaded values and currently selected values
                                const valueOptions = [...loadedValueOptions];
                                filter.value.forEach((selectedValue) => {
                                    if (!valueOptions.some((option) => option.value === selectedValue)) {
                                        valueOptions.push({ label: selectedValue, value: selectedValue });
                                    }
                                });
                                const currentValues = valueOptions.filter((option) => filter.value.includes(option.value));
                                // Create a unique key from the filter's properties
                                const uniqueKey = `${filter.field}-${filter.operator}-${filter.value.sort().join('|')}`;
                                return (_jsxs("div", { className: 'flex gap-4 items-end', children: [_jsxs("div", { className: 'grid grid-cols-3 gap-4 flex-1', children: [_jsx(Select, { label: translate('Filter by'), placeholder: translate('--Select--'), options: filterFieldOptions, value: currentField, onChange: (option) => {
                                                        const updatedFilters = [...filters];
                                                        updatedFilters[filters.findIndex((f) => f.field === filter.field &&
                                                            f.operator === filter.operator &&
                                                            f.value.join('|') === filter.value.join('|'))] = {
                                                            ...updatedFilters[filters.findIndex((f) => f.field === filter.field &&
                                                                f.operator === filter.operator &&
                                                                f.value.join('|') === filter.value.join('|'))],
                                                            field: option.value,
                                                            value: [],
                                                        };
                                                        setFilters(updatedFilters);
                                                    }, width: 'full' }), _jsx(Select, { label: translate('Operator'), placeholder: '', options: operatorOptions, value: currentOperator, onChange: (option) => handleFilterChange(filters.findIndex((f) => f.field === filter.field &&
                                                        f.operator === filter.operator &&
                                                        f.value.join('|') === filter.value.join('|')), 'operator', option.value), width: 'full' }), _jsx(Select, { label: translate('Value'), placeholder: isLoading ? translate('Loading...') : translate('--Select--'), options: valueOptions, value: currentValues, onChange: (selectedOptions) => {
                                                        const selectedValues = selectedOptions.map((option) => option.value);
                                                        handleFilterChange(filters.findIndex((f) => f.field === filter.field &&
                                                            f.operator === filter.operator &&
                                                            f.value.join('|') === filter.value.join('|')), 'value', selectedValues);
                                                    }, width: 'full', disabled: isLoading || !filter.field, multiple: true, selectionLabel: currentValues.length > 0 ? currentValues.map((v) => v.label).join(', ') : undefined, showMultipleSelectionBadges: false })] }), _jsxs("div", { className: 'flex items-center gap-1', children: [_jsx("div", { className: 'flex items-center justify-center cursor-pointer hover:opacity-70 pb-2 w-8', onClick: () => handleReloadFilterValues(filters.findIndex((f) => f.field === filter.field &&
                                                        f.operator === filter.operator &&
                                                        f.value.join('|') === filter.value.join('|'))), title: translate('Reload filter values'), children: _jsx("span", { className: 'text-xs text-primary font-bold', children: "\u21BB" }) }), _jsx("div", { className: 'flex items-center justify-center cursor-pointer hover:opacity-70 pb-2 w-8', onClick: () => handleRemoveFilter(filters.findIndex((f) => f.field === filter.field &&
                                                        f.operator === filter.operator &&
                                                        f.value.join('|') === filter.value.join('|'))), title: translate('Delete filter'), children: _jsx(Icon, { name: 'delete', size: 'sm', color: 'red' }) })] })] }, uniqueKey));
                            }), _jsxs("div", { className: 'flex items-center gap-2 text-primary text-sm cursor-pointer', onClick: handleAddFilter, children: [_jsx(Icon, { name: 'add', size: 'sm', color: 'primary' }), _jsx("span", { children: translate('Add filter') })] })] })] }), _jsxs("section", { children: [_jsxs("div", { className: 'flex justify-between items-center my-8', children: [_jsx("div", { children: translate('Rename') }), _jsx(Button, { label: translate('Reset renaming'), variant: 'secondary', onClick: handleResetRenaming })] }), _jsx("div", { children: _jsx(Table, { columns: renameColumns, data: sortedFieldsForTable }) })] })] }));
};
